import React, { useState, useEffect, useRef } from "react";
import '../style.css'; // whatever your CSS file is

const STIM_FILES = [ "Stim1.mp3", "Stim2.mp3", "Stim3.mp3", "Stim4.mp3" ];

export default function PomotoneClock({ onStartMusic, onStopMusic }) {
const [secondsLeft, setSecondsLeft] = useState(15 * 60);
const [isRunning, setIsRunning]   = useState(false);
const [showBreakPrompt, setShowBreakPrompt] = useState(false);
const [isBreak, setIsBreak] = useState(false);
const [showCoffeeFollowup, setShowCoffeeFollowup] = useState(null);

const audioRef = useRef(null);

  // Try to autoplay ambience on mount
  useEffect(() => {
    const ambience = document.getElementById("ambience");
    if (ambience) {
      ambience.volume = 1;
      ambience.load();
      ambience.play().catch(() => {
        console.log("Autoplay blocked — waiting for user interaction to play ambience.");
      });
    }
  }, []);

  // Add one-time user interaction fallback for ambience autoplay
  useEffect(() => {
    const handleInteraction = () => {
      const ambience = document.getElementById("ambience");
      if (ambience && ambience.paused) {
        ambience.play();
      }
      document.removeEventListener("click", handleInteraction);
    };
    document.addEventListener("click", handleInteraction);
    return () => document.removeEventListener("click", handleInteraction);
  }, []);

const pickRandomStim = () => {
  const idx = Math.floor(Math.random() * STIM_FILES.length);
  return `/assets/audio/${STIM_FILES[idx]}`;
};

// handle play/pause
useEffect(() => {
  if (isRunning) {
    if (!audioRef.current || audioRef.current.ended) {
      audioRef.current = new Audio(pickRandomStim());
    }
    audioRef.current.play();
    onStartMusic?.();
  } else {
    if (audioRef.current) {
      audioRef.current.pause();
    }
  }
}, [isRunning, onStartMusic]);

// countdown
useEffect(() => {
  if (!isRunning) return;
  const timer = setInterval(() => {
    setSecondsLeft(s => {
      if (s <= 1) {
        clearInterval(timer);
        const bell = document.getElementById("bell");
        if (bell) bell.play();

        if (
          audioRef.current &&
          !audioRef.current.src.includes("ambience.mp3") &&
          typeof audioRef.current.volume === "number"
        ) {
          const fade = setInterval(() => {
            if (audioRef.current && audioRef.current.volume > 0.05) {
              audioRef.current.volume -= 0.05;
            } else if (audioRef.current) {
              audioRef.current.volume = 0;
              audioRef.current.pause();
              audioRef.current.currentTime = 0;
              audioRef.current = null;
              clearInterval(fade);
            }
          }, 50);
        }

        if (!isBreak) {
          setShowBreakPrompt(true);
          setIsRunning(false); // pause so break prompt can be accepted
        } else {
          setIsRunning(false);
          setIsBreak(false);
          if (audioRef.current) {
            const fade = setInterval(() => {
              if (audioRef.current && audioRef.current.volume > 0.05) {
                audioRef.current.volume -= 0.05;
              } else if (audioRef.current) {
                audioRef.current.volume = 0;
                audioRef.current.pause();
                audioRef.current.currentTime = 0;
                audioRef.current = null;
                clearInterval(fade);
              }
            }, 50);
          }
        }

        return 0;
      }

      return s - 1;
    });
  }, 1000);
  return () => clearInterval(timer);
}, [isRunning]);

  // Support skipping to break with "b" key
  useEffect(() => {
    const skipToBreak = (e) => {
      if (e.key === 'b') {
        setIsRunning(false);
        setSecondsLeft(1);
      }
    };
    document.addEventListener('keydown', skipToBreak);
    return () => document.removeEventListener('keydown', skipToBreak);
  }, []);

useEffect(() => {
  if (showCoffeeFollowup) {
    const timeout = setTimeout(() => {
      setShowCoffeeFollowup(null);
    }, 15000);
    return () => clearTimeout(timeout);
  }
}, [showCoffeeFollowup]);

const handlePlayPause = () => {
  if (secondsLeft === 0) return;
  setIsRunning(r => !r);
};

const handleReset = () => {
  setIsRunning(false);
  setSecondsLeft(15 * 60);
  setIsBreak(false);
  setShowBreakPrompt(false);
  setShowCoffeeFollowup(null);
  if (audioRef.current) {
    audioRef.current.pause();
    audioRef.current.currentTime = 0;
    audioRef.current = null;
    onStopMusic?.();
  }
};

const formatTime = s => {
  const m = Math.floor(s / 60), sec = s % 60;
  return `${m}:${sec < 10 ? "0" : ""}${sec}`;
};

const startBreak = () => {
  setSecondsLeft(5 * 60);
  setIsBreak(true);
  setShowBreakPrompt(false);
  setShowCoffeeFollowup("yes");

  if (audioRef.current) {
    audioRef.current.pause();
  }
  audioRef.current = new Audio('/assets/audio/break.mp3');
  audioRef.current.volume = 0;
  audioRef.current.play().catch((e) => console.warn("break.mp3 failed to play", e));

  const fadeIn = setInterval(() => {
    if (audioRef.current && audioRef.current.volume < 0.95) {
      audioRef.current.volume += 0.05;
    } else if (audioRef.current) {
      audioRef.current.volume = 1;
      clearInterval(fadeIn);
    }
  }, 50);

  // ensure timer starts after setting up audio
  setIsRunning(true);
};

const declineBreak = () => {
  setSecondsLeft(15 * 60);
  setIsBreak(false);
  setIsRunning(false);
  setShowBreakPrompt(false);
  setShowCoffeeFollowup("no");
};

return (
  <>
    <div className="pomotone-clock">
      <img
        className="clock-image"
        src="/assets/images/Clock_Base.png"
        alt="Pomotone timer"
      />

      <div className="clock-time">{formatTime(secondsLeft)}</div>

      <div className="clock-buttons">
        <button onClick={handlePlayPause}>
          <img
            src={`/assets/icons/${isRunning ? "pause" : "play"}.svg`}
            alt={isRunning ? "Pause" : "Play"}
            width={36}
            height={36}
          />
        </button>
        <button onClick={handleReset}>
          <img
            src="/assets/icons/reset.svg"
            alt="Reset"
            width={36}
            height={36}
          />
        </button>
      </div>
    </div>

    {showBreakPrompt && (
      <div className="break-prompt">
        <p>Need a coffee break?</p>
        <button onClick={startBreak}>Yes</button>
        <button onClick={declineBreak}>No</button>
      </div>
    )}

    {showCoffeeFollowup === "yes" && (
      <div className="break-prompt">
        <p>Enjoy your coffee! (<a href="https://buymeacoffee.com/pipindigo" target="_blank">Buy me one too!</a>)</p>
      </div>
    )}
    {showCoffeeFollowup === "no" && (
      <div className="break-prompt">
        <p>No problem! (<a href="https://buymeacoffee.com/pipindigo" target="_blank">I'd love one though!</a>)</p>
      </div>
    )}

    {isBreak && secondsLeft === 0 && (
      <div className="coffee-prompt">
        Enjoying your coffee? <a href="https://buymeacoffee.com/yourname" target="_blank">Buy me one too!</a>
      </div>
    )}
  </>
);
}
